<template>
  <div>
    <span style="font-weight: bolder;">{{ fragment.label }}</span>
    <label class="label required" v-if="fragment.required">必須</label>
    <label class="label optional" v-else>任意</label>

    <v-text-field
      v-if="fragment.type === 'TX'"
      single-line
      outlined
      :error-messages="hasError"
      :value="fragment.value"
      :disabled="isDeactivate"
      :readonly="isConfirmed"
      :background-color="backgroundColor"
      @input="onUpdateFragment">
    </v-text-field>
    <v-textarea
      v-if="fragment.type === 'TA'"
      single-line
      outlined
      auto-grow
      :disabled="isDeactivate"
      :value="fragment.value"
      :readonly="isConfirmed"
      :background-color="backgroundColor"
      @input="onUpdateFragment"
    ></v-textarea>
    <v-select
      v-if="fragment.type === 'SL' && fragment.auto_generate_options"
      v-model="selectedItem"
      :items="items"
      outline
      solo
      :disabled="isDeactivate || isConfirmed"
      :background-color="backgroundColor"
      @change="onUpdateFragment">
    </v-select>
    <template v-if="fragment.type === 'FL'">
      <v-text-field single-line solo enclosed light readonly
                    type="text"
                    placeholder="ファイル選択"
                    @click="pickFile"
                    v-model="imageName"
                    prepend-inner-icon="attach_file"
                    :disabled="isDeactivate || isConfirmed"
                    :background-color="backgroundColor">
      </v-text-field>
      <input type="file" @change="onPickedFile" style="display: none;" ref="file"
             :disabled="isDeactivate || isConfirmed"/>
    </template>

    <fragment-options
      v-if="['RA', 'CK'].includes(fragment.type) || ( fragment.type==='SL' && !fragment.auto_generate_options )"
      :fragment="fragment"
      @updateFragment="onUpdateFragment"
      :isDeactivate="isDeactivate"
      :isConfirmed="isConfirmed">
    </fragment-options>

  </div>
</template>

<script>
import Enumerable from 'linq'
import FragmentOptions from '@/components/molecules/FragmentOptions'
import {
  FragmentActivationSetting,
  FragmentActivationRule,
  FragmentActivationCondition,
  FragmentActivationValue,
  FormItemFragment
} from '@/model/models'

export default {
  name: 'FormItemFragment',
  components: {
    FragmentOptions
  },
  props: {
    fragment: Object,
    fragmentIdsIsActives: Object,
    isConfirmed: Boolean
  },
  data () {
    return {
      selectedItem: '選択して下さい',
      imageName: '',
      imageUrl: '',
			imageFile: ''
    }
  },
  computed: {
    hasError () {
      let count = Enumerable.from(this.$store.getters['sheet/invalidFragments'])
        .where(invalidFragment => invalidFragment.id === this.fragment.id)
        .toArray().length

      if (count > 0) {
        return '必ず入力して下さい'
      }
    },
    items () {
      if (!this.fragment.auto_generate_options) return []

      const [type, rangeBegin, rangeEnd] = this.fragment.auto_generate_options.split(':')

      if (type === 'range') {
        return this.generateOptions(rangeBegin, rangeEnd)
      }

      if (type === 'year_range') {
        let now = new Date()
        let year = now.getFullYear()

        let from = this.cutOffRange(year - 200, year + parseInt(rangeBegin), year + 200)
        let to = this.cutOffRange(year - 200, year + parseInt(rangeEnd), year + 200)
        if (from >= to) {
          to = from + 1
        }

        return this.generateOptions(from, to)
      }

      if (type === 'past_year') {
        let now = new Date()
        let year = now.getFullYear()
        return this.generateOptions(year - 20, year)
      }

      if (type === 'month') {
        return this.generateOptions(1, 12)
      }

      if (type === 'day') {
        return this.generateOptions(1, 31)
      }

      if (type === 'hour12') {
        return this.generateOptions(0, 11)
      }

      if (type === 'hour24') {
        return this.generateOptions(0, 23)
      }

      if (type === 'minute') {
        return this.generateOptions(0, 59)
      }
    },
    fragmentActivations () {
      let fragmentActivations = {
        setting: null
      }

      const fragmentActivationSetting = FragmentActivationSetting.query()
        .where('target_form_item_fragment', this.fragment.id).first()
      if (fragmentActivationSetting) {
        fragmentActivations.setting = fragmentActivationSetting
      } else {
        return fragmentActivations
      }

      fragmentActivations.rules = []
      const fragmentActivationRules = FragmentActivationRule.query()
        .where('fragment_activation_setting', fragmentActivationSetting.id).get()
      for (const fragmentActivationRule of fragmentActivationRules) {
        let rule = {
          rule: fragmentActivationRule,
          conditions: []
        }
        const fragmentActivationConditions = FragmentActivationCondition.query()
          .where('fragment_activation_rule', fragmentActivationRule.id).get()
        for (const fragmentActivationCondition of fragmentActivationConditions) {
          let condition = {
            condition: fragmentActivationCondition,
            values: FragmentActivationValue.query().where('fragment_activation_condition', fragmentActivationCondition.id).get()
          }
          rule.conditions.push(condition)
        }
        fragmentActivations.rules.push(rule)
      }
      return fragmentActivations
    },
    isDeactivate () {
      // 有効無効条件が設定されていないfragmentは無条件で活性にする
      if (!this.fragmentActivations.setting) {
        return false
      }

      let isMatchRule = false
      for (const rule of this.fragmentActivations.rules) {
        isMatchRule = this.isMatchFragmentActivationRule(rule)
        // ルール間はORなので、一つでも満たすとチェックを終える
        if (isMatchRule) {
          break
        }
      }

      const isDeactivate = !(this.fragmentActivations.setting.default_action === 'activate')

      this.$emit('updateFragmentIdsIsActives', this.fragment.id, isMatchRule ? !isDeactivate : isDeactivate)

      return isMatchRule ? isDeactivate : !isDeactivate
    },
    backgroundColor () {
      /**
       * 背景色の変更
       */
      if (this.isConfirmed) {
        return '#D3D3D3'
      } else if (this.isDeactivate) {
        return '#f5f5f5'
      }
      return undefined
    }
  },
  methods: {
    generateOptions (start, end) {
      let options = ['選択して下さい']
      for (let option = start; option <= end; option++) {
        options.push(option.toString())
      }
      return options
    },
    cutOffRange (min, value, max) {
      return Math.max(Math.min(value, max), min)
    },
    onUpdateFragment (updatedValue) {
      if (updatedValue === '選択して下さい') {
        this.$emit('updateFragment', {
          id: this.fragment.id,
          form_item: this.fragment.form_item,
          value: null
        })
      } else {
        this.$emit('updateFragment', {
          id: this.fragment.id,
          form_item: this.fragment.form_item,
          updatedValue
        })
      }
    },
    pickFile () {
      this.$refs.file.click()
    },
    onPickedFile (e) {
			const files = e.target.files
      const pickedFile = files[0]
			if (pickedFile) {
				this.imageName = pickedFile.name
        console.log(this.imageName)
				if (this.imageName.lastIndexOf('.') <= 0) {
					return
				}

				const fr = new FileReader ()
				fr.readAsDataURL(pickedFile)
				fr.addEventListener('load', () => {
					this.imageUrl = fr.result
					this.imageFile = pickedFile
				})
			}
			else {
				this.imageName = ''
        this.imageUrl = ''
				this.imageFile = ''
			}
    },
    isMatchFragmentActivationRule (rule) {
      for (const { condition, values } of rule.conditions) {
        // 条件間はANDなので一つでもFalseになるとFalseを返す
        if (!this.isMatchFragmentActivationCondition(condition, values)) {
          return false
        }
      }
      return true
    },
    isMatchFragmentActivationCondition (condition, values) {
      const conditionalFormItemFragment = FormItemFragment.query()
        .where('id', condition.conditional_form_item_fragment).first()
      if (values.length === 1) {
        return this.isMatchSingleFragmentActivationValue(conditionalFormItemFragment, values[0], condition.operator)
      } else {
        return this.isMatchMultipleFragmentActivationValues(conditionalFormItemFragment, values, condition.operator)
      }
    },
    isMatchSingleFragmentActivationValue (conditionalFormItemFragment, fragmentActivationValue, operator) {
      if (!this.fragmentIdsIsActives[conditionalFormItemFragment.id]) {
        // 設定した項目が非活性の場合は有効無効条件を満たしていないとする（数珠繋ぎに条件が設定されていることを考慮）
        return false
      }
      if (['TX', 'TA'].includes(conditionalFormItemFragment.type)) {
        return this.isMatchTextTypeFragmentActivationValue(conditionalFormItemFragment, fragmentActivationValue, operator)
      } else if (['RA', 'SL', 'CK'].includes(conditionalFormItemFragment.type)) {
        if (conditionalFormItemFragment.auto_generate_options) {
          return this.isMatchNumericRangeFragmentActivationValue(conditionalFormItemFragment, fragmentActivationValue, operator)
        } else {
          return this.isMatchChoiceFragmentActivationValue(conditionalFormItemFragment, fragmentActivationValue, operator)
        }
      }
      return true
    },
    isMatchMultipleFragmentActivationValues (conditionalFormItemFragment, fragmentActivationValues, operator) {
      if (operator === 'or') {
        // operatorがor（いずれかを満たす）のときは一つでも条件にマッチすればtrueを返す
        for (const fragmentActivationValue of fragmentActivationValues) {
          // 複数「条件・選択肢・数値範囲」のとき、operatorは「match」固定
          if (this.isMatchSingleFragmentActivationValue(conditionalFormItemFragment, fragmentActivationValue, 'match')) {
            return true
          }
        }
        return false
      } else if (operator === 'and') {
        // operatorがand（全て満たす）のときは一つでも条件にマッチしないとfalseを返す
        for (const fragmentActivationValue of fragmentActivationValues) {
          // 複数「条件・選択肢・数値範囲」のとき、operatorは「match」固定
          if (!this.isMatchSingleFragmentActivationValue(conditionalFormItemFragment, fragmentActivationValue, 'match')) {
            return false
          }
        }
        return true
      } else {
        // ここに来ることはないが、バグを考慮してfalseを返す
        return false
      }
    },
    isMatchTextTypeFragmentActivationValue (conditionalFormItemFragment, fragmentActivationValue, operator) {
      switch (operator) {
        case 'match':
          return conditionalFormItemFragment.value === fragmentActivationValue.conditional_value
        case 'mismatch':
          return conditionalFormItemFragment.value !== fragmentActivationValue.conditional_value
        case 'blank':
          return !conditionalFormItemFragment.value
        case 'non_blank':
          return !!conditionalFormItemFragment.value
        case 'including':
          if (conditionalFormItemFragment.value) {
            return conditionalFormItemFragment.value.search(fragmentActivationValue.conditional_value) >= 0
          } else {
            return false
          }
        default:
          // ここに来ることはないが、バグを考慮してfalseを返す
          return false
      }
    },
    isMatchChoiceFragmentActivationValue (conditionalFormItemFragment, fragmentActivationValue, operator) {
      switch (operator) {
        case 'match':
          if (conditionalFormItemFragment.type === 'CK') {
            if (conditionalFormItemFragment.value.length > 0) {
              return conditionalFormItemFragment.value.includes(fragmentActivationValue.conditional_value)
            } else {
              return false
            }
          } else {
            return conditionalFormItemFragment.value === fragmentActivationValue.conditional_value
          }
        case 'mismatch':
          if (conditionalFormItemFragment.type === 'CK') {
            if (conditionalFormItemFragment.value.length > 0) {
              return !conditionalFormItemFragment.value.includes(fragmentActivationValue.conditional_value)
            } else {
              return true
            }
          } else {
            return conditionalFormItemFragment.value !== fragmentActivationValue.conditional_value
          }
        case 'blank':
          if (conditionalFormItemFragment.type === 'CK') {
            return conditionalFormItemFragment.value.length === 0
          } else {
            return !conditionalFormItemFragment.value
          }
        case 'non_blank':
          if (conditionalFormItemFragment.type === 'CK') {
            return conditionalFormItemFragment.value.length > 0
          } else {
            return !!conditionalFormItemFragment.value
          }
        default:
          // ここに来ることはないが、バグを考慮してfalseを返す
          return false
      }
    },
    isMatchNumericRangeFragmentActivationValue (conditionalFormItemFragment, fragmentActivationValue, operator) {
      let rangeStart = null
      let rangeEnd = null
      if (fragmentActivationValue.conditional_value && ['match', 'mismatch'].includes(operator)) {
        const range = fragmentActivationValue.conditional_value.split(',')
        rangeStart = range[0]
        rangeEnd = range[1]
      }

      switch (operator) {
        case 'match':
          if (!conditionalFormItemFragment.value) {
            return false
          }
          return rangeStart <= conditionalFormItemFragment.value && rangeEnd >= conditionalFormItemFragment.value
        case 'mismatch':
          if (!conditionalFormItemFragment.value) {
            return true
          }
          return rangeStart > conditionalFormItemFragment.value || rangeEnd < conditionalFormItemFragment.value
        case 'blank':
          return !conditionalFormItemFragment.value
        case 'non_blank':
          return !!conditionalFormItemFragment.value
        default:
          // ここに来ることはないが、バグを考慮してfalseを返す
          return false
      }
    }
  }
}
</script>

<style scoped>
.label {
  display: inline-block;
  padding: 0 5px;
  height: 22px;
  line-height: 20px;
  font-size: 12px;
  border-radius: 4px;
  box-sizing: border-box;
  white-space: nowrap;
  margin-bottom: 5px;
}

.required {
  background-color: rgba(245, 108, 108, .1);
  border-color: rgba(245, 108, 108, .2);
  color: #f56c6c;
}

.optional {
  background-color: rgba(64, 158, 255, .1);
  color: #409eff;
  border: 1px solid rgba(64, 158, 255, .2);
}
</style>
